import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  usePublishAction,
  useArchiveAction,
  useAuditLog,
  useRollbackVersion,
} from "@/hooks/useContentOperations";
import {
  seoSchema,
  pageContentSchema,
  validateNoindex,
  validateContentStructure,
  type SEOData,
} from "@/lib/contentValidation";
import { ServiceBlock, DEFAULT_BLOCK_ORDER } from "@/lib/serviceBlocks";
import AdminHeader from "@/components/admin/AdminHeader";
import ServiceBlockEditor from "@/components/admin/ServiceBlockEditor";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Save,
  Send,
  Archive,
  Eye,
  FileText,
  Search,
  CheckCircle,
  Clock,
  AlertTriangle,
  History,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Copy,
} from "lucide-react";

interface PageRow {
  id: string;
  slug: string;
  page_title: string;
  content: Record<string, any>;
  status: string;
  updated_at: string;
  created_by: string | null;
  // NOTE: page_content does NOT have seo, page_type, or updated_by columns.
  // SEO data is stored inside the content JSONB under a "seo" key.
  seo?: Record<string, any>;
}

interface VersionRow {
  id: string;
  snapshot: Record<string, any>;
  created_at: string;
  created_by: string | null;
}

const statusColors: Record<string, string> = {
  published: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  draft: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  review: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  archived: "bg-muted text-muted-foreground",
};

const statusIcons: Record<string, React.ElementType> = {
  published: CheckCircle,
  draft: Clock,
  review: Eye,
  archived: Archive,
};

const PageContentManager = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [editedContent, setEditedContent] = useState<Record<string, any>>({});
  const [seoData, setSeoData] = useState<SEOData>({
    title: "",
    description: "",
    canonicalUrl: "",
    ogTitle: "",
    ogDescription: "",
    ogImage: "",
    indexable: true,
  });
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [blocks, setBlocks] = useState<ServiceBlock[]>([]);

  const publishAction = usePublishAction();
  const archiveAction = useArchiveAction();
  const logAudit = useAuditLog();
  const rollbackVersion = useRollbackVersion();

  // Fetch all pages
  const { data: pages, isLoading } = useQuery({
    queryKey: ["admin-pages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("page_content")
        .select("*")
        .order("page_title");
      if (error) throw error;
      return data as unknown as PageRow[];
    },
  });

  // Fetch versions for selected page
  const { data: versions } = useQuery({
    queryKey: ["content-versions", selectedId],
    queryFn: async () => {
      if (!selectedId) return [];
      const { data, error } = await supabase
        .from("content_versions")
        .select("*")
        .eq("entity_type", "page_content")
        .eq("entity_id", selectedId)
        .order("created_at", { ascending: false })
        .limit(5);
      if (error) throw error;
      return data as VersionRow[];
    },
    enabled: !!selectedId,
  });

  const selectedPage = pages?.find((p) => p.id === selectedId);

  // Load page data when selection changes
  useEffect(() => {
    if (selectedPage) {
      const contentData = selectedPage.content || {};
      const { _seo, _blocks, ...pageContent } = contentData as Record<string, any>;
      setEditedContent(pageContent);
      setBlocks(_blocks || []);
      const seo = _seo || {};
      setSeoData({
        title: seo.title || "",
        description: seo.description || "",
        canonicalUrl: seo.canonicalUrl || `/${selectedPage.slug}`,
        ogTitle: seo.ogTitle || "",
        ogDescription: seo.ogDescription || "",
        ogImage: seo.ogImage || "",
        indexable: seo.indexable !== false,
        schemaType: seo.schemaType || "",
      });
      setExpandedSections([]);
      setValidationErrors([]);
    }
  }, [selectedId, selectedPage?.updated_at]);

  // Filter pages
  const filteredPages = pages?.filter((p) => {
    const matchesStatus = statusFilter === "all" || p.status === statusFilter;
    const matchesSearch =
      !searchTerm ||
      p.page_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.slug.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  // Save content + SEO (does NOT publish)
  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPage || !user) throw new Error("No page selected");

      // Validate content structure
      const contentErrors = validateContentStructure(editedContent);
      if (contentErrors.length > 0) {
        setValidationErrors(contentErrors);
        throw new Error(contentErrors.join(", "));
      }

      // Validate noindex guardrail
      const noindexError = validateNoindex(selectedPage.slug, seoData.indexable);
      if (noindexError) {
        setValidationErrors([noindexError]);
        throw new Error(noindexError);
      }

      setValidationErrors([]);

      // Store SEO and blocks inside content JSONB
      const contentWithMeta: Record<string, any> = { ...editedContent, _seo: seoData };
      if (blocks.length > 0) contentWithMeta._blocks = blocks;

      const { error } = await supabase
        .from("page_content")
        .update({
          content: contentWithMeta,
        })
        .eq("id", selectedPage.id);
      if (error) throw error;

      await logAudit({
        action: "save",
        entityType: "page_content",
        entityId: selectedPage.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-pages"] });
      queryClient.invalidateQueries({ queryKey: ["page-content", selectedPage?.slug] });
      toast.success("Changes saved");
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Duplicate page
  const duplicateMutation = useMutation({
    mutationFn: async (page: PageRow) => {
      if (!user) throw new Error("Not authenticated");

      const newSlug = `${page.slug}-copy-${Date.now().toString(36)}`;
      const { data, error } = await supabase
        .from("page_content")
        .insert({
          slug: newSlug,
          page_title: `${page.page_title} (Copy)`,
          content: page.content,
          status: "draft",
          created_by: user.id,
        })
        .select("id")
        .single();
      if (error) throw error;

      await logAudit({
        action: "duplicate",
        entityType: "page_content",
        entityId: data.id,
        metadata: { source_page_id: page.id, source_slug: page.slug },
      });

      return data.id;
    },
    onSuccess: (newId) => {
      queryClient.invalidateQueries({ queryKey: ["admin-pages"] });
      setSelectedId(newId);
      toast.success("Page duplicated as draft");
    },
    onError: (err: any) => toast.error("Duplicate failed: " + err.message),
  });

  // Status change (draft, review)
  const changeStatusMutation = useMutation({
    mutationFn: async (newStatus: string) => {
      if (!selectedPage || !user) return;
      const { error } = await supabase
        .from("page_content")
        .update({ status: newStatus, updated_by: user.id })
        .eq("id", selectedPage.id);
      if (error) throw error;
      await logAudit({
        action: `status_change_${newStatus}`,
        entityType: "page_content",
        entityId: selectedPage.id,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-pages"] });
      toast.success("Status updated");
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Publish with validation
  const handlePublish = async () => {
    if (!selectedPage) return;

    // Validate SEO before publish
    const seoResult = seoSchema.safeParse(seoData);
    if (!seoResult.success) {
      const errors = seoResult.error.errors.map((e) => e.message);
      setValidationErrors(errors);
      toast.error("Fix SEO validation errors before publishing");
      return;
    }

    // Validate noindex
    const noindexError = validateNoindex(selectedPage.slug, seoData.indexable);
    if (noindexError) {
      setValidationErrors([noindexError]);
      toast.error(noindexError);
      return;
    }

    // Save first, then publish
    await saveMutation.mutateAsync();
    publishAction.mutate({
      id: selectedPage.id,
      currentContent: editedContent,
      currentSeo: seoData,
    });
  };

  // Content field rendering
  const toggleSection = (key: string) => {
    setExpandedSections((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const updateField = (path: string, value: any) => {
    setEditedContent((prev) => {
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let obj = copy;
      for (let i = 0; i < keys.length - 1; i++) {
        const key = isNaN(Number(keys[i])) ? keys[i] : Number(keys[i]);
        obj = obj[key];
      }
      const lastKey = isNaN(Number(keys[keys.length - 1]))
        ? keys[keys.length - 1]
        : Number(keys[keys.length - 1]);
      obj[lastKey] = value;
      return copy;
    });
  };

  const renderField = (key: string, value: any, path: string): React.ReactNode => {
    if (key === "icon" || key === "accentColor") return null; // Skip system fields

    if (typeof value === "string") {
      const isLong = value.length > 100;
      return (
        <div key={path} className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {key}
          </label>
          {isLong ? (
            <Textarea
              value={value}
              onChange={(e) => updateField(path, e.target.value)}
              rows={3}
              className="text-sm"
            />
          ) : (
            <Input
              value={value}
              onChange={(e) => updateField(path, e.target.value)}
              className="text-sm"
            />
          )}
        </div>
      );
    }

    if (typeof value === "boolean") {
      return (
        <div key={path} className="flex items-center justify-between">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {key}
          </label>
          <Switch
            checked={value}
            onCheckedChange={(v) => updateField(path, v)}
          />
        </div>
      );
    }

    if (typeof value === "number") {
      return (
        <div key={path} className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            {key}
          </label>
          <Input
            type="number"
            value={value}
            onChange={(e) => updateField(path, Number(e.target.value))}
            className="text-sm"
          />
        </div>
      );
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return null;
      if (typeof value[0] === "string") {
        return (
          <div key={path} className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {key}
            </label>
            {value.map((item, i) => (
              <Input
                key={`${path}.${i}`}
                value={item}
                onChange={(e) => updateField(`${path}.${i}`, e.target.value)}
                className="text-sm"
              />
            ))}
          </div>
        );
      }
      return (
        <div key={path} className="space-y-3">
          <button
            onClick={() => toggleSection(path)}
            className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
          >
            {expandedSections.includes(path) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            {key} ({value.length} items)
          </button>
          {expandedSections.includes(path) && (
            <div className="ml-4 space-y-4 border-l border-border pl-4">
              {value.map((item: any, i: number) => (
                <div key={`${path}.${i}`} className="space-y-2 bg-muted/30 rounded-lg p-3">
                  <span className="text-xs font-medium text-muted-foreground">
                    {item.name || item.title || item.label || item.question || `Item ${i + 1}`}
                  </span>
                  {Object.entries(item).map(([k, v]) => renderField(k, v, `${path}.${i}.${k}`))}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (typeof value === "object" && value !== null) {
      return (
        <div key={path} className="space-y-3">
          <button
            onClick={() => toggleSection(path)}
            className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors"
          >
            {expandedSections.includes(path) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            {key}
          </button>
          {expandedSections.includes(path) && (
            <div className="ml-4 space-y-3 border-l border-border pl-4">
              {Object.entries(value).map(([k, v]) => renderField(k, v, `${path}.${k}`))}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const formatDate = (d: string) => {
    return new Date(d).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen">
      <AdminHeader
        title="Page Content Manager"
        description="Manage all page content, SEO, and publishing lifecycle"
      />

      <main className="p-6">
        <div className="flex gap-6">
          {/* Left: Page List */}
          <div className="w-72 flex-shrink-0 space-y-4">
            {/* Filters */}
            <div className="space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search pages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 text-sm"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="text-sm">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="review">In Review</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Page list */}
            <div className="bg-card border rounded-lg divide-y divide-border max-h-[calc(100vh-220px)] overflow-y-auto">
              {isLoading ? (
                <div className="p-3 space-y-2">
                  {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : filteredPages?.length === 0 ? (
                <p className="text-sm text-muted-foreground p-4 text-center">No pages found</p>
              ) : (
                filteredPages?.map((page) => {
                  const StatusIcon = statusIcons[page.status] || FileText;
                  return (
                    <button
                      key={page.id}
                      onClick={() => setSelectedId(page.id)}
                      className={`w-full text-left px-3 py-3 text-sm transition-colors ${
                        selectedId === page.id ? "bg-primary/10" : "hover:bg-accent"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <StatusIcon className={`h-3.5 w-3.5 flex-shrink-0 ${
                          page.status === "published" ? "text-green-500" :
                          page.status === "draft" ? "text-yellow-500" :
                          page.status === "review" ? "text-blue-500" : "text-muted-foreground"
                        }`} />
                        <span className="font-medium truncate">{page.page_title}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 ml-5.5">
                        <span className="text-xs text-muted-foreground">/{page.slug}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusColors[page.status]}`}>
                          {page.status}
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Right: Editor */}
          <div className="flex-1 min-w-0">
            {!selectedPage ? (
              <Card>
                <CardContent className="py-16 text-center">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Select a page to edit</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Header bar with actions */}
                <Card>
                  <CardContent className="py-4 px-5 space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <h2 className="text-lg font-semibold truncate">{selectedPage.page_title}</h2>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs text-muted-foreground font-mono">/{selectedPage.slug}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusColors[selectedPage.status]}`}>
                            {selectedPage.status}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            Updated {formatDate(selectedPage.updated_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button variant="outline" size="sm" asChild>
                        <a
                          href={`/${selectedPage.slug === "homepage" ? "" : selectedPage.slug}?preview=true`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Preview
                        </a>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => saveMutation.mutate()}
                        disabled={saveMutation.isPending}
                      >
                        <Save className="h-4 w-4 mr-1" />
                        Save
                      </Button>
                      {selectedPage.status !== "review" && selectedPage.status !== "archived" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => changeStatusMutation.mutate("review")}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Send to Review
                        </Button>
                      )}
                      {selectedPage.status !== "published" && selectedPage.status !== "archived" && (
                        <Button
                          size="sm"
                          onClick={handlePublish}
                          disabled={publishAction.isPending}
                        >
                          <Send className="h-4 w-4 mr-1" />
                          Publish
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => duplicateMutation.mutate(selectedPage)}
                        disabled={duplicateMutation.isPending}
                      >
                        <Copy className="h-4 w-4 mr-1" />
                        Duplicate
                      </Button>
                      <div className="flex-1" />
                      {selectedPage.status !== "archived" && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => archiveAction.mutate(selectedPage.id)}
                        >
                          <Archive className="h-4 w-4 mr-1" />
                          Archive
                        </Button>
                      )}
                      {selectedPage.status === "archived" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => changeStatusMutation.mutate("draft")}
                        >
                          Restore to Draft
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Validation errors */}
                {validationErrors.length > 0 && (
                  <Card className="border-destructive">
                    <CardContent className="py-3 px-4">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-destructive mt-0.5" />
                        <div className="space-y-1">
                          {validationErrors.map((err, i) => (
                            <p key={i} className="text-sm text-destructive">{err}</p>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Tabs: Sections / Content / SEO / Versions */}
                <Tabs defaultValue="sections">
                  <TabsList>
                    <TabsTrigger value="sections">Sections</TabsTrigger>
                    <TabsTrigger value="content">Content</TabsTrigger>
                    <TabsTrigger value="seo">SEO</TabsTrigger>
                    <TabsTrigger value="versions">
                      <History className="h-4 w-4 mr-1" />
                      Versions ({versions?.length || 0})
                    </TabsTrigger>
                  </TabsList>

                  {/* Sections Tab - Block ordering */}
                  <TabsContent value="sections">
                    <Card>
                      <CardContent className="py-4">
                        <ServiceBlockEditor
                          blocks={blocks.length > 0 ? blocks : DEFAULT_BLOCK_ORDER}
                          onChange={setBlocks}
                        />
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Content Tab */}
                  <TabsContent value="content">
                    <Card>
                      <CardContent className="py-4 space-y-4">
                        {Object.entries(editedContent).map(([key, value]) =>
                          renderField(key, value, key)
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* SEO Tab */}
                  <TabsContent value="seo">
                    <Card>
                      <CardContent className="py-4 space-y-4">
                        <div className="space-y-2">
                          <Label>
                            SEO Title
                            <span className={`ml-2 text-xs ${seoData.title.length > 60 ? "text-destructive" : "text-muted-foreground"}`}>
                              ({seoData.title.length}/60)
                            </span>
                          </Label>
                          <Input
                            value={seoData.title}
                            onChange={(e) => setSeoData((s) => ({ ...s, title: e.target.value }))}
                            placeholder="Page title for search engines"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>
                            Meta Description
                            <span className={`ml-2 text-xs ${
                              seoData.description.length > 160 ? "text-destructive" :
                              seoData.description.length < 140 && seoData.description.length > 0 ? "text-yellow-500" : "text-muted-foreground"
                            }`}>
                              ({seoData.description.length}/160)
                            </span>
                          </Label>
                          <Textarea
                            value={seoData.description}
                            onChange={(e) => setSeoData((s) => ({ ...s, description: e.target.value }))}
                            placeholder="Brief description for search results (140-160 chars)"
                            rows={3}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Canonical URL</Label>
                          <Input
                            value={seoData.canonicalUrl}
                            onChange={(e) => setSeoData((s) => ({ ...s, canonicalUrl: e.target.value }))}
                            placeholder="/page-slug"
                          />
                        </div>

                        <div className="flex items-center justify-between py-2">
                          <div>
                            <Label>Allow Search Indexing</Label>
                            <p className="text-xs text-muted-foreground">
                              Allow search engines to index this page
                            </p>
                          </div>
                          <Switch
                            checked={seoData.indexable}
                            onCheckedChange={(v) => setSeoData((s) => ({ ...s, indexable: v }))}
                          />
                        </div>

                        <div className="border-t pt-4">
                          <h3 className="font-medium mb-4">Open Graph (Social Sharing)</h3>
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <Label>OG Title</Label>
                              <Input
                                value={seoData.ogTitle}
                                onChange={(e) => setSeoData((s) => ({ ...s, ogTitle: e.target.value }))}
                                placeholder="Defaults to SEO title"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>OG Description</Label>
                              <Textarea
                                value={seoData.ogDescription}
                                onChange={(e) => setSeoData((s) => ({ ...s, ogDescription: e.target.value }))}
                                rows={2}
                                placeholder="Defaults to meta description"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>OG Image URL</Label>
                              <Input
                                value={seoData.ogImage}
                                onChange={(e) => setSeoData((s) => ({ ...s, ogImage: e.target.value }))}
                                placeholder="https://..."
                              />
                            </div>
                          </div>
                        </div>

                        <div className="border-t pt-4">
                          <div className="space-y-2">
                            <Label>Schema Type</Label>
                            <Select
                              value={seoData.schemaType || ""}
                              onValueChange={(v) => setSeoData((s) => ({ ...s, schemaType: v }))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select schema type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Organization">Organization</SelectItem>
                                <SelectItem value="Service">Service</SelectItem>
                                <SelectItem value="WebPage">WebPage</SelectItem>
                                <SelectItem value="FAQPage">FAQ Page</SelectItem>
                                <SelectItem value="Article">Article</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Versions Tab */}
                  <TabsContent value="versions">
                    <Card>
                      <CardContent className="py-4">
                        {!versions || versions.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-8">
                            No versions saved yet. Versions are created on each publish.
                          </p>
                        ) : (
                          <div className="space-y-3">
                            {versions.map((version) => (
                              <div
                                key={version.id}
                                className="flex items-center justify-between p-3 border rounded-lg"
                              >
                                <div>
                                  <p className="text-sm font-medium">
                                    {formatDate(version.created_at)}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    Snapshot at publish time
                                  </p>
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    rollbackVersion.mutate({
                                      versionId: version.id,
                                      entityType: "page_content",
                                      entityId: selectedPage.id,
                                    })
                                  }
                                  disabled={rollbackVersion.isPending}
                                >
                                  <RotateCcw className="h-4 w-4 mr-1" />
                                  Restore
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default PageContentManager;
